import { InsecureServerUrlError, isInsecureServerUrl, requireServerUrl } from '@wiztime/sync';

/** The origin that serves this page; the default sync server. */
export function currentOrigin(): string {
  return window.location.origin;
}

/**
 * The server URL as the sync configuration stores it: empty for this page's own origin (so the
 * configuration keeps working if the site moves), otherwise the normalised URL. Throws
 * `InvalidServerUrlError` for anything that is not an http(s) URL, and `InsecureServerUrlError`
 * for an http address that a page served over https cannot reach.
 */
export function toConfigUrl(input: string): string {
  const url = requireServerUrl(input);
  return url === requireServerUrl(currentOrigin()) ? '' : url;
}

/** Why a server address cannot be used. */
export const ServerUrlProblem = {
  /** Empty, not a URL, or not http(s). */
  Invalid: 'invalid',
  /** An http address on a page served over https: the browser blocks it. */
  BlockedInsecure: 'blockedInsecure',
} as const;
export type ServerUrlProblem = (typeof ServerUrlProblem)[keyof typeof ServerUrlProblem];

/** The problem of `input` as a server address, or null when it is usable. `pageProtocol` is `location.protocol`. */
export function serverUrlProblem(
  input: string,
  pageProtocol: string = window.location.protocol,
): ServerUrlProblem | null {
  if (input.trim() === '') return ServerUrlProblem.Invalid;
  try {
    requireServerUrl(input, pageProtocol);
    return null;
  } catch (error) {
    return error instanceof InsecureServerUrlError
      ? ServerUrlProblem.BlockedInsecure
      : ServerUrlProblem.Invalid;
  }
}

/** Whether `input` is a usable server address. */
export function isValidServerUrl(input: string, pageProtocol?: string): boolean {
  return serverUrlProblem(input, pageProtocol) === null;
}

/** Whether a usable `input` sends the access token unencrypted (http to another machine): worth a warning. */
export function warnsInsecure(input: string, pageProtocol?: string): boolean {
  if (!isValidServerUrl(input, pageProtocol)) return false;
  return isInsecureServerUrl(requireServerUrl(input, pageProtocol));
}
